import { useEffect, useState, type FormEvent, type MouseEvent } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import type { Template } from '@sp/shared'
import { api } from '../lib/api.ts'
import { useStores, useTrips } from '../lib/groupData.ts'
import { HttpError } from '../lib/http.ts'
import { groupInitials, itemsCount, plural, tripName, tripSub } from '../lib/format.ts'
import { openTrips } from '../lib/offerToTrip.ts'
import { enablePush, pushState, type PushState } from '../lib/push.ts'
import { TripStart } from './TripStart.tsx'
import { useConfirm } from './ConfirmSheet.tsx'
import { errorMessage } from '../lib/useResource.ts'
import { Icon } from './Icon.tsx'
import { Sheet } from './Sheet.tsx'
import { Button, Checkbox, IconButton, Paragraph, SelectRow, TextCol, TextField } from './ui.tsx'

/** Sablony (navrh v2): dlazdice, seznam, prepinac hlidani akci a panely kolem sablony. */

const failure = (err: unknown) =>
  err instanceof HttpError && err.isOffline ? 'Šablony jde měnit jen se signálem.' : errorMessage(err)

/** Barva dlazdice: Pravidelny nakup zlaty, ostatni podle nazvu, at se neprehazuji. */
export function templateTone(template: Pick<Template, 'name' | 'regular'>): number {
  if (template.regular) return 0
  let hash = 0
  for (const char of template.name) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 9973
  return 1 + (hash % 4)
}

export function TemplateTile({ template, small = false }: { template: Pick<Template, 'name' | 'regular'>; small?: boolean }) {
  return (
    <span className={`tpl-tile tone-${templateTone(template)}${small ? ' is-small' : ''}`} aria-hidden="true">
      {groupInitials(template.name)}
    </span>
  )
}

/** Seznam sablon vlevo na strance sablony (pocitac). */
export function TemplateList({
  templates,
  onNew,
  onMenu,
}: {
  templates: readonly Template[]
  onNew(): void
  /** Prave tlacitko na sablone. */
  onMenu?: ((template: Template) => (event: MouseEvent<HTMLElement>) => void) | undefined
}) {
  return (
    <nav className="glass-panel tpl-list" aria-label="Šablony">
      <div className="tpl-list-head">
        <h2 className="panel-title">Šablony</h2>
        <IconButton icon="plus" label="Nová šablona" bordered onClick={onNew} />
      </div>
      {templates.map((template) => (
        <NavLink
          key={template.id}
          to={`/sablony/${template.id}`}
          className="tpl-list-link"
          title={template.name}
          aria-label={template.name}
          onContextMenu={onMenu?.(template)}
        >
          <TemplateTile template={template} small />
          <span className="desk-text">
            <span className="desk-text-title">{template.name}</span>
            <span className="desk-text-sub">
              {[itemsCount(template.items.length), template.watchOffers ? 'upozorní na akce' : null].filter(Boolean).join(' · ')}
            </span>
          </span>
        </NavLink>
      ))}
    </nav>
  )
}

/**
 * „Upozornit na akce" (navrh v3, drive „Hlídat akce"). Prepinac plati pro
 * skupinu, upozorneni ale chodi jen do zarizeni, kde jsou zapnuta — kdyz tady
 * nejsou, radek nabidne zapnout je rovnou.
 */
export function WatchRow({ template, onToggle }: { template: Template; onToggle(): void }) {
  const [device, setDevice] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    pushState()
      .then((value) => live && setDevice(value))
      .catch(() => live && setDevice('unsupported'))
    return () => {
      live = false
    }
  }, [])

  async function enable() {
    setBusy(true)
    try {
      setDevice(await enablePush())
    } catch {
      setDevice('off')
    } finally {
      setBusy(false)
    }
  }

  const deviceNote =
    template.watchOffers && device === 'off'
      ? 'Na tomhle zařízení upozornění nemáš.'
      : template.watchOffers && device === 'denied'
        ? 'Upozornění má prohlížeč zakázaná – povol je v nastavení webu.'
        : null

  return (
    <div className="stack-4">
      <button type="button" className="tpl-watch" role="switch" aria-checked={template.watchOffers} onClick={onToggle}>
        <Icon name="bell" size={20} />
        <TextCol
          title="Upozornit na akce"
          sub={
            template.watchOffers
              ? 'Když bude něco odsud v letáku, dáme vědět (i v Akcích)'
              : 'Zapni, až se příležitost bude blížit'
          }
        />
        <span className={`switch${template.watchOffers ? ' is-on' : ''}`} aria-hidden="true" />
      </button>
      {deviceNote && (
        <div className="watch-device">
          <Paragraph size="meta" tone="fg">
            {deviceNote}
          </Paragraph>
          {device === 'off' && (
            <Button compact disabled={busy} onClick={() => void enable()}>
              Zapnout tady
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export function NewTemplateSheet({
  open,
  onClose,
  groupId,
  onCreated,
}: {
  open: boolean
  onClose(): void
  groupId: string
  onCreated(template: Template): void
}) {
  const [name, setName] = useState('')
  const [watch, setWatch] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName('')
    setWatch(false)
    setError(null)
  }, [open])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return setError('Chybí název')
    setBusy(true)
    setError(null)
    try {
      const created = await api.templates.create(groupId, { name: name.trim(), watchOffers: watch })
      onClose()
      onCreated(created)
    } catch (err) {
      setError(failure(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Nová šablona" subtitle="Zboží, které se kupuje spolu">
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <TextField
          label="Název"
          placeholder="Třeba Grilovačka nebo Špagety"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          autoFocus
        />
        <button type="button" className="store-pick" aria-pressed={watch} onClick={() => setWatch((v) => !v)}>
          <Checkbox checked={watch} />
          <TextCol title="Upozornit na akce" sub="Když bude něco z ní v letáku, dáme vědět" />
        </button>
        {error && <Paragraph tone="error">{error}</Paragraph>}
        <Button kind="primary" type="submit" full disabled={busy}>
          Vytvořit šablonu
        </Button>
      </form>
    </Sheet>
  )
}

export function TemplateSettingsSheet({
  open,
  onClose,
  template,
  canCopy,
  onSaved,
  onDeleted,
  onCopy,
}: {
  open: boolean
  onClose(): void
  template: Template
  canCopy: boolean
  onSaved(template: Template): void
  onDeleted(): void
  onCopy(): void
}) {
  const [name, setName] = useState(template.name)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName(template.name)
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(failure(err))
    } finally {
      setBusy(false)
    }
  }

  const { confirm, confirmSheet } = useConfirm()

  async function remove() {
    const ok = await confirm({
      title: `Smazat šablonu „${template.name}“?`,
      text: 'Zboží zůstane ve skupině i v ostatních šablonách.',
      confirmLabel: 'Smazat šablonu',
      danger: true,
    })
    if (!ok) return
    void run(async () => {
      await api.templates.archive(template.id)
      onClose()
      onDeleted()
    })
  }

  return (
    <>
      <Sheet open={open} onClose={onClose} title="Nastavení šablony" subtitle={template.name}>
        <div className="stack">
          <form
            className="add-row align-end"
            onSubmit={(e) => {
              e.preventDefault()
              if (!name.trim()) return setError('Chybí název')
              void run(async () => {
                onSaved(await api.templates.update(template.id, { name: name.trim() }))
                onClose()
              })
            }}
          >
            <TextField label="Název" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className="grow" />
            <Button type="submit" disabled={busy || name.trim() === template.name}>
              Uložit
            </Button>
          </form>
          {canCopy && (
            <Button icon="copy" className="self-start" onClick={onCopy}>
              Kopírovat do jiné skupiny
            </Button>
          )}
          {template.regular ? (
            <Paragraph size="meta">Pravidelný nákup je základ výběru při plánování – smazat nejde.</Paragraph>
          ) : (
            <Button kind="danger" compact className="self-start" disabled={busy} onClick={() => void remove()}>
              Smazat šablonu
            </Button>
          )}
          {error && <Paragraph tone="error">{error}</Paragraph>}
        </div>
      </Sheet>
      {confirmSheet}
    </>
  )
}

/**
 * „Nakoupit" ze sablony (navrh v3): rozpracovane nakupy, nebo rovnou novy nakup —
 * drive tu byla slepa ulicka „Založ ho na stránce Nákupy". Pak vyber ze sablony
 * s predvybranym zbozim.
 */
export function AddTemplateToTripSheet({
  open,
  onClose,
  groupId,
  template,
}: {
  open: boolean
  onClose(): void
  groupId: string
  template: Template
}) {
  const navigate = useNavigate()
  const trips = useTrips(groupId)
  const stores = useStores(groupId, true)
  const allStores = stores.res.state === 'ok' ? stores.res.data : []
  const list = openTrips(trips.res.state === 'ok' ? trips.res.data : [])
  const [creating, setCreating] = useState(false)
  const count = template.items.length
  const pick = (tripId: string) => {
    setCreating(false)
    onClose()
    navigate(`/nakup/${tripId}/sablona?sablona=${template.id}`)
  }

  return (
    <Sheet
      open={open}
      onClose={() => {
        setCreating(false)
        onClose()
      }}
      title="Do kterého nákupu?"
      subtitle={template.name}
    >
      <div className="stack-8">
        {!creating &&
          list.map((trip) => (
            <SelectRow key={trip.id} as="button" pad="even" onClick={() => pick(trip.id)}>
              <TextCol title={tripName(trip, allStores).title} sub={tripSub(trip, allStores)} />
              <Icon name="chevron_right" size={20} className="tone-muted" />
            </SelectRow>
          ))}
        {creating ? (
          <TripStart
            groupId={groupId}
            stores={allStores}
            defaultTitle={template.regular ? '' : template.name}
            submitLabel={() => `Vybrat z ${plural(count, 'položky', 'položek', 'položek')}`}
            onCreated={(trip) => pick(trip.id)}
          />
        ) : (
          <SelectRow as="button" pad="even" onClick={() => setCreating(true)}>
            <TextCol title="Nový nákup" sub="vybereš obchod a den" />
            <Icon name="plus" size={20} className="tone-accent" />
          </SelectRow>
        )}
      </div>
    </Sheet>
  )
}
